import { useState } from 'react';
import { CheckCircleOutlined } from '@mui/icons-material';
import {
	FormActions,
	FormCard,
	FormField,
	FormInput,
	FormSelect,
	FormSectionTitle,
} from '@practium/ui';
import { useFosfatagemTab } from '../../hooks/useFosfatagemTab';
import { SimpleDataTable } from '../Tables/SimpleDataTable/SimpleDataTable';
import { RESULT_TABLE_COLUMNS, mapResultTableRows } from '../../utils/correctionTableMapper';
import {
	FOSFATAGEM_FORMA_OPTIONS,
	DOSE_SECUNDARIA_LABEL,
	getOptionLabel,
} from '../../constants/correction.constants';
import { PanelActions } from '../PanelActions/PanelActions';
import styles from './TabPanel.module.css';

interface FosfatagemTabProps {
	canEdit?: boolean;
}

export const FosfatagemTab = ({ canEdit = true }: FosfatagemTabProps) => {
	const {
		form,
		ajuste,
		result,
		hasCalculated,
		isCalculating,
		isSaving,
		errors,
		set,
		setAjuste,
		handleCalculate,
		handleSave,
		handleClear,
		fertilizerOptions,
		setComplementaryDose,
		handleAdjustDoses,
		isAdjusting,
	} = useFosfatagemTab();

	const [isSaved, setIsSaved] = useState(() => hasCalculated && !!form.fertilizanteP);

	const doseSecLabel = form.forma ? DOSE_SECUNDARIA_LABEL[form.forma] : '';
	const showLargura = form.forma === 'sulco' || form.forma === 'faixa' || form.forma === 'cova';
	const showComprimento = form.forma === 'cova';

	const handleSaveAndView = async () => {
		const saved = await handleSave();
		if (saved) setIsSaved(true);
	};

	if (isSaved) {
		return (
			<div className={styles.panel}>
				<FormCard icon={<CheckCircleOutlined />} title="Fosfatagem">
					<div className={styles.form}>
						<div className={styles.section}>
							<FormSectionTitle>Cálculo</FormSectionTitle>
							<div className={styles.sectionFields}>
								<div className={styles.row2}>
									<FormField label="Fertilizante para correção de P">
										<span className={styles.viewText}>
											{getOptionLabel(fertilizerOptions, form.fertilizanteP)}
										</span>
									</FormField>
									<FormField label="Fertilizante complementar">
										<span className={styles.viewText}>
											{getOptionLabel(fertilizerOptions, form.fertilizanteComplementar)}
										</span>
									</FormField>
								</div>
								<div className={styles.row2}>
									<FormField label="Forma de aplicação">
										<span className={styles.viewText}>
											{getOptionLabel(FOSFATAGEM_FORMA_OPTIONS, form.forma)}
										</span>
									</FormField>
									{form.forma && (
										<FormField label="Profundidade (m)">
											<span className={styles.viewText}>{form.profundidade || '—'}</span>
										</FormField>
									)}
								</div>
								{showLargura && (
									<div className={showComprimento ? styles.row2 : styles.halfLeft}>
										<FormField label="Largura (m)">
											<span className={styles.viewText}>{form.largura || '—'}</span>
										</FormField>
										{showComprimento && (
											<FormField label="Comprimento (m)">
												<span className={styles.viewText}>{form.comprimento || '—'}</span>
											</FormField>
										)}
									</div>
								)}
							</div>
						</div>

						<div className={styles.section}>
							<FormSectionTitle>Ajuste</FormSectionTitle>
							<div className={styles.sectionFields}>
								<div className={doseSecLabel ? styles.row2 : styles.halfLeft}>
									<FormField label="Dose (kg/ha)">
										<span className={styles.viewText}>{ajuste.dose || '—'}</span>
									</FormField>
									{doseSecLabel && (
										<FormField label={doseSecLabel}>
											<span className={styles.viewText}>{ajuste.doseSecundaria || '—'}</span>
										</FormField>
									)}
								</div>
								{form.fertilizanteComplementar && (
									<div className={doseSecLabel ? styles.row2 : styles.halfLeft}>
										<FormField label="Dose complementar (kg/ha)">
											<span className={styles.viewText}>{ajuste.doseComplementar || '—'}</span>
										</FormField>
										{doseSecLabel && (
											<FormField label={doseSecLabel}>
												<span className={styles.viewText}>
													{ajuste.doseSecundariaComplementar || '—'}
												</span>
											</FormField>
										)}
									</div>
								)}
								{result && (
									<div className={styles.row3}>
										<FormField label="N fornecido (kg/ha)">
											<span className={styles.viewText}>{result.N || '—'}</span>
										</FormField>
										<FormField label="P2O5 fornecido (kg/ha)">
											<span className={styles.viewText}>{result.P2O5 || '—'}</span>
										</FormField>
										<FormField label="K2O fornecido (kg/ha)">
											<span className={styles.viewText}>{result.K2O || '—'}</span>
										</FormField>
									</div>
								)}
							</div>
						</div>

						{result && (
							<div className={styles.section}>
								<FormSectionTitle>Resultado</FormSectionTitle>
								<SimpleDataTable columns={RESULT_TABLE_COLUMNS} rows={mapResultTableRows(result.table)} />
							</div>
						)}
					</div>
				</FormCard>

				<PanelActions>
					<FormActions
						mode="view"
						onDelete={
							canEdit
								? () => {
										handleClear();
										setIsSaved(false);
									}
								: undefined
						}
						onEdit={canEdit ? () => setIsSaved(false) : undefined}
					/>
				</PanelActions>
			</div>
		);
	}

	return (
		<div className={styles.panel}>
			<FormCard icon={<CheckCircleOutlined />} title="Fosfatagem">
				<div className={styles.form}>
					<div className={styles.section}>
						<FormSectionTitle>Cálculo</FormSectionTitle>
						<div className={styles.sectionFields}>
							<div className={styles.row2}>
								<FormField
									label="Fertilizante para correção de P"
									required
									error={errors.fertilizanteP}
								>
									<FormSelect
										value={form.fertilizanteP}
										onChange={(v) => set('fertilizanteP', v)}
										options={fertilizerOptions}
										placeholder="Selecione o fertilizante"
										hasError={!!errors.fertilizanteP}
									/>
								</FormField>
								<FormField label="Fertilizante complementar">
									<FormSelect
										clearable
										value={form.fertilizanteComplementar}
										onChange={(v) => set('fertilizanteComplementar', v)}
										options={fertilizerOptions}
										placeholder="Selecione a fertilizante"
									/>
								</FormField>
							</div>

							<div className={styles.row2}>
								<FormField label="Forma de aplicação" required error={errors.forma}>
									<FormSelect
										value={form.forma}
										onChange={(v) => {
											set('forma', v);
											set('largura', '');
											set('comprimento', '');
										}}
										options={FOSFATAGEM_FORMA_OPTIONS}
										placeholder="Selecione a aplicação"
										hasError={!!errors.forma}
									/>
								</FormField>
								{form.forma && (
									<FormField label="Profundidade (m)" required error={errors.profundidade}>
										<FormInput
											placeholder="Ex.: 0.4"
											type="number"
											value={form.profundidade}
											onChange={(e) => set('profundidade', e.target.value)}
											hasError={!!errors.profundidade}
										/>
									</FormField>
								)}
							</div>

							{showLargura && (
								<div className={showComprimento ? styles.row2 : styles.halfLeft}>
									<FormField label="Largura (m)" required error={errors.largura}>
										<FormInput
											placeholder="Ex.: 0.4"
											type="number"
											value={form.largura}
											onChange={(e) => set('largura', e.target.value)}
											hasError={!!errors.largura}
										/>
									</FormField>
									{showComprimento && (
										<FormField label="Comprimento (m)" required error={errors.comprimento}>
											<FormInput
												placeholder="Ex.: 0.4"
												type="number"
												value={form.comprimento}
												onChange={(e) => set('comprimento', e.target.value)}
												hasError={!!errors.comprimento}
											/>
										</FormField>
									)}
								</div>
							)}
						</div>
					</div>

					{hasCalculated && (
						<>
							<div className={styles.section}>
								<FormSectionTitle>Ajuste</FormSectionTitle>
								<div className={styles.sectionFields}>
									<div className={doseSecLabel ? styles.row2 : styles.halfLeft}>
										<FormField label="Dose (kg/ha)" required>
											<FormInput
												type="number"
												value={ajuste.dose}
												onChange={(e) => setAjuste('dose', e.target.value)}
												onBlur={handleAdjustDoses}
												disabled={isAdjusting}
											/>
										</FormField>
										{doseSecLabel && (
											<FormField label={doseSecLabel}>
												<FormInput
													value={ajuste.doseSecundaria}
													readOnly
													className={styles.readonlyInput}
												/>
											</FormField>
										)}
									</div>
									{result && (
										<div className={styles.row3}>
											<FormField label="N fornecido (kg/ha)">
												<FormInput value={result.N} readOnly className={styles.readonlyInput} />
											</FormField>
											<FormField label="P2O5 fornecido (kg/ha)">
												<FormInput value={result.P2O5} readOnly className={styles.readonlyInput} />
											</FormField>
											<FormField label="K2O fornecido (kg/ha)">
												<FormInput value={result.K2O} readOnly className={styles.readonlyInput} />
											</FormField>
										</div>
									)}
								</div>
								{form.fertilizanteComplementar && (
									<div className={doseSecLabel ? styles.row2 : styles.halfLeft}>
										<FormField label="Dose complementar (kg/ha)" required>
											<FormInput
												type="number"
												value={ajuste.doseComplementar || ''}
												onChange={(e) => setComplementaryDose(e.target.value)}
												onBlur={handleAdjustDoses}
												disabled={isAdjusting}
											/>
										</FormField>
										{doseSecLabel && (
											<FormField label={doseSecLabel}>
												<FormInput
													value={ajuste.doseSecundariaComplementar || ''}
													readOnly
													className={styles.readonlyInput}
												/>
											</FormField>
										)}
									</div>
								)}
							</div>

							{result && (
								<div className={styles.section}>
									<FormSectionTitle>Resultado</FormSectionTitle>
									<SimpleDataTable columns={RESULT_TABLE_COLUMNS} rows={mapResultTableRows(result.table)} />
								</div>
							)}
						</>
					)}
				</div>
			</FormCard>

			<PanelActions>
				<FormActions
					onClear={() => {
						handleClear();
						setIsSaved(false);
					}}
					onSecondary={handleCalculate}
					secondaryText={hasCalculated ? 'Recalcular' : 'Calcular'}
					secondaryDisabled={isCalculating}
					secondaryLoading={isCalculating}
					onSave={handleSaveAndView}
					isLoading={isSaving}
					saveDisabled={!hasCalculated}
				/>
			</PanelActions>
		</div>
	);
};
